import { flush as sentryFlush } from '@sentry/react'

import { isChunkLoadError, isStylesheetPreloadError } from './chunkLoadError'
import { captureMessage } from './errorReport'

declare global {
  interface WindowEventMap {
    'vite:preloadError': Event & { payload?: unknown }
  }
}

/** A chunk error is worth one reload: past that we have just tried it, and it did not help. */
export const shouldReloadForChunkError = (err: unknown): boolean => isChunkLoadError(err) && !hasReloadedRecently()

export const reloadForChunkError = () => {
  markReloaded()
  void sentryFlush(2000).finally(() => {
    window.location.reload()
  })
}

export const installChunkLoadErrorReload = () => {
  // Vite throws when a chunk's stylesheet fails, which takes the component down with it over
  // nothing — a tap on a time slot, on a connection that dropped for a moment. Prevented, the
  // import still resolves and the component renders, unstyled at worst. A js chunk must keep
  // throwing: swallowed, its import resolves as `undefined` and breaks somewhere less obvious.
  window.addEventListener('vite:preloadError', event => {
    if (!isStylesheetPreloadError(event.payload)) return
    event.preventDefault()
    captureMessage('Stylesheet preload failed', {
      level: 'warning',
      extra: { asset: event.payload instanceof Error ? event.payload.message : String(event.payload) },
    })
  })
  window.addEventListener('error', event => {
    if (shouldReloadForChunkError(event.error)) reloadForChunkError()
  })
  window.addEventListener('unhandledrejection', event => {
    if (shouldReloadForChunkError(event.reason)) reloadForChunkError()
  })
}

const RELOAD_KEY = 'kidsout:chunk-reload-at'
const RELOAD_COOLDOWN_MS = 60_000

// sessionStorage can throw in Safari private mode or with cookies blocked. Reload anyway then —
// a stale chunk without a reload is worse than one reload too many.
const hasReloadedRecently = (): boolean => {
  try {
    return Date.now() - Number(sessionStorage.getItem(RELOAD_KEY) ?? 0) < RELOAD_COOLDOWN_MS
  } catch {
    return false
  }
}

const markReloaded = () => {
  try {
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()))
  } catch {
    // see hasReloadedRecently
  }
}

export { isChunkLoadError, isStylesheetPreloadError } from './chunkLoadError'
